import type {
  Alert,
  AlertImage,
  AlertImageDto,
  AlertCreate,
  AlertDto,
  AlertReference,
  AlertReferenceDto,
  AlertTypeSummary,
  AlertTypeSummaryDto,
  AlertUpdate,
  AlertUpdateDto,
} from '@src/features/alerts/types';
import { shortUserFromDto } from '@src/features/users/lib/user';

const ALERT_TITLE_MAX_LENGTH = 80;

function getAlertTitleFromDescription(description: string): string {
  const normalizedDescription = description.trim().replace(/\s+/g, ' ');
  if (normalizedDescription.length <= ALERT_TITLE_MAX_LENGTH) {
    return normalizedDescription;
  }

  return `${normalizedDescription.slice(0, ALERT_TITLE_MAX_LENGTH - 3)}...`;
}

function alertTypeFromDto(alertType: AlertTypeSummaryDto): AlertTypeSummary {
  return {
    id: alertType.id,
    name: alertType.name,
    icon: alertType.icon,
    sort: alertType.alty_sort,
  };
}

function imageFromDto(image: AlertImageDto): AlertImage {
  return {
    id: image.id,
    image: image.image,
  };
}

function referenceFromDto(reference: AlertReferenceDto): AlertReference {
  return {
    contentType: reference.content_type,
    objectId: reference.object_id,
    label: reference.label,
    type: reference.type,
  };
}

export function alertFromDto(alert: AlertDto): Alert {
  return {
    id: alert.id,
    type: alertTypeFromDto(alert.aler_alty),
    title: getAlertTitleFromDescription(alert.aler_description),
    description: alert.aler_description,
    resolutionNotes: alert.aler_notes ?? '',
    resolvedAt: alert.aler_completion_time,
    creator: shortUserFromDto(alert.aler_user_creator),
    user: shortUserFromDto(alert.aler_user_pic),
    status: alert.alrt_status,
    reference: referenceFromDto(alert.reference),
    images: alert.images.map(imageFromDto),
    canEdit: alert.can_edit,
    canReassign: alert.can_reassign,
    canResolve: alert.can_resolve,
    editableFields: alert.editable_fields,
    createdAt: alert.created_at,
    updatedAt: alert.updated_at,
  };
}

function hasOwn<T extends object, K extends PropertyKey>(
  value: T,
  key: K,
): value is T & Record<K, unknown> {
  return Object.hasOwn(value, key);
}

export function alertUpdateToDto(
  alert: Partial<AlertUpdate>,
): Partial<AlertUpdateDto> {
  const payload: Partial<AlertUpdateDto> = {};

  if (hasOwn(alert, 'description')) {
    payload.aler_description = alert.description as string;
  }
  if (hasOwn(alert, 'resolutionNotes')) {
    payload.aler_notes = alert.resolutionNotes as string;
  }

  return payload;
}

export function alertCreateToFormData(alert: AlertCreate): FormData {
  const formData = new FormData();
  formData.append('aler_alty', String(alert.typeId));
  formData.append('aler_description', alert.description);
  formData.append('aler_user_pic', String(alert.userId));

  if (
    alert.reference
    && alert.reference.contentTypeId !== null
    && alert.reference.objectId !== null
  ) {
    formData.append('content_type_id', String(alert.reference.contentTypeId));
    formData.append('object_id', String(alert.reference.objectId));
  }

  alert.images.forEach((image) => {
    formData.append('images', image);
  });

  return formData;
}
